import { inject, injectable } from 'tsyringe';
import { BaseViewModel } from '@/shared/viewmodels/BaseViewModel';
import { NotificationsApiService } from './NotificationsApiService';
import type { NotificationActivityEntry } from './NotificationTypes';

/**
 * State behind Settings → Notifications → Activity: the tenant-wide record of
 * what has been sent.
 *
 * Loads lazily, when the tab is first opened — most visits to this screen are to
 * change a preference, and the activity query spans every recipient in the
 * tenant. A permission failure is surfaced distinctly from a general error, so
 * the screen can say "you can't see this" rather than "something broke".
 */
@injectable()
export class NotificationActivityViewModel extends BaseViewModel {
    private entryList: NotificationActivityEntry[] = [];
    private isLoading = false;
    private hasLoaded = false;
    private hasFailed = false;
    private isForbidden = false;

    constructor(@inject(NotificationsApiService) private readonly api: NotificationsApiService) {
        super();
    }

    get entries(): NotificationActivityEntry[] {
        return this.entryList;
    }

    get loading(): boolean {
        return this.isLoading;
    }

    get failed(): boolean {
        return this.hasFailed;
    }

    get forbidden(): boolean {
        return this.isForbidden;
    }

    /** Loads once per mount. Safe to call on every tab change. */
    async loadOnce(): Promise<void> {
        if (this.hasLoaded || this.isLoading) return;
        await this.load();
    }

    async load(): Promise<void> {
        this.isLoading = true;
        this.notifyChange();
        try {
            const page = await this.api.getActivity();
            this.entryList = page.entries;
            this.hasFailed = false;
            this.isForbidden = false;
        } catch (error) {
            this.isForbidden = error instanceof Error && error.message.includes('(403)');
            this.hasFailed = !this.isForbidden;
        }
        this.hasLoaded = true;
        this.isLoading = false;
        this.notifyChange();
    }
}
